import { FormControl, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { REPORT_LEVEL_OPTIONS } from "../../../../utils/technicalReportLevel";
import {
  renderCircleValue,
  renderDivisionValue,
  renderSubDivisionValue,
} from "../../../Report/coaHierarchySelectRenderers";
import { ADMIN_COMPANY_SELECT_OPTIONS } from "../../../../constants/discoCompanies";

const FilterFieldLabel = ({ htmlFor, children }) => (
  <Typography
    component="label"
    variant="body2"
    htmlFor={htmlFor}
    className="field-label"
    sx={filterFieldLabelSx}
  >
    {children}
  </Typography>
);

FilterFieldLabel.propTypes = {
  htmlFor: PropTypes.string,
  children: PropTypes.node,
};

const filterFieldLabelSx = {
  fontWeight: 600,
  display: "block",
  minHeight: "1.35rem",
  lineHeight: 1.35,
  mb: 0.5,
};

const filterControlSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
  },
};

/** MUI Report Level + conditional hierarchy filters for complaint list reports. */
export default function ComplaintsReportLevelFields({
  idPrefix = "pc",
  code,
  reportLevel,
  onReportLevelChange,
  availableReportLevelOptions = REPORT_LEVEL_OPTIONS,
  selectedCompanyCode,
  onChangeCompany,
  showCircleFilter,
  circleCode,
  onChangeCircle,
  filteredCircles = [],
  circleFilterEnabled = true,
  showDivisionFilter,
  selectedDivision,
  onChangeDivision,
  filteredDivisions = [],
  divisionFilterEnabled = false,
  showSubDivisionFilter,
  selectedSubDivision,
  onChangeSubDivision,
  filteredSubDivisions = [],
  subDivisionFilterEnabled = false,
}) {
  return (
    <>
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <FilterFieldLabel htmlFor={`${idPrefix}-report-level`}>
            Report Level
          </FilterFieldLabel>
          <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
            <Select
              id={`${idPrefix}-report-level`}
              value={reportLevel}
              onChange={onReportLevelChange}
              disabled={availableReportLevelOptions.length <= 1}
            >
              {availableReportLevelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Grid>

      {String(code) === "10" && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-company`}>Company</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-company`}
                displayEmpty
                value={selectedCompanyCode || ""}
                onChange={onChangeCompany}
              >
                <MenuItem value="">
                  <em>Select Company</em>
                </MenuItem>
                {ADMIN_COMPANY_SELECT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}

      {showCircleFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-circle`}>Circle</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-circle`}
                displayEmpty
                value={circleCode || ""}
                onChange={onChangeCircle}
                disabled={!circleFilterEnabled}
                renderValue={(v) => renderCircleValue(v, filteredCircles)}
              >
                <MenuItem value="">
                  <em>All Circle</em>
                </MenuItem>
                {filteredCircles.map((circle) => (
                  <MenuItem key={circle.circleCode} value={circle.circleCode}>
                    {circle.circleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}

      {showDivisionFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-division`}>Division</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-division`}
                displayEmpty
                value={selectedDivision || ""}
                onChange={onChangeDivision}
                disabled={!divisionFilterEnabled}
                renderValue={(v) => renderDivisionValue(v, filteredDivisions)}
              >
                <MenuItem value="">
                  <em>All Division</em>
                </MenuItem>
                {filteredDivisions.map((division) => (
                  <MenuItem key={division.divisionCode} value={division.divisionCode}>
                    {division.divisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}

      {showSubDivisionFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-subdiv`}>
              Sub-Division
            </FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-subdiv`}
                displayEmpty
                value={selectedSubDivision || ""}
                onChange={onChangeSubDivision}
                disabled={!subDivisionFilterEnabled}
                renderValue={(v) =>
                  renderSubDivisionValue(v, filteredSubDivisions)
                }
              >
                <MenuItem value="">
                  <em>All Sub-Division</em>
                </MenuItem>
                {filteredSubDivisions.map((subDivision) => (
                  <MenuItem
                    key={subDivision.subDivisionCode}
                    value={subDivision.subDivisionCode}
                  >
                    {subDivision.subDivisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
    </>
  );
}

ComplaintsReportLevelFields.propTypes = {
  idPrefix: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportLevel: PropTypes.string,
  onReportLevelChange: PropTypes.func.isRequired,
  availableReportLevelOptions: PropTypes.array,
  selectedCompanyCode: PropTypes.string,
  onChangeCompany: PropTypes.func,
  showCircleFilter: PropTypes.bool,
  circleCode: PropTypes.string,
  onChangeCircle: PropTypes.func,
  filteredCircles: PropTypes.array,
  circleFilterEnabled: PropTypes.bool,
  showDivisionFilter: PropTypes.bool,
  selectedDivision: PropTypes.string,
  onChangeDivision: PropTypes.func,
  filteredDivisions: PropTypes.array,
  divisionFilterEnabled: PropTypes.bool,
  showSubDivisionFilter: PropTypes.bool,
  selectedSubDivision: PropTypes.string,
  onChangeSubDivision: PropTypes.func,
  filteredSubDivisions: PropTypes.array,
  subDivisionFilterEnabled: PropTypes.bool,
};
